import React from "react";
import {
    Button,
    Div,
    Group,
    ModalPage,
    ModalPageHeader,
    ModalRoot,
    PanelHeaderButton,
    SimpleCell,
    SplitCol,
    SplitLayout,
    Title
} from "@vkontakte/vkui";
import {
    Icon20Check,
    Icon20RecordingCircleFill,
    Icon20RefreshOutline,
    Icon24Cancel,
    Icon24Stop,
    Icon24Video,
    Icon28CameraOutline,
    Icon28UploadOutline
} from "@vkontakte/icons";

import BaseComponent from "./BaseComponent";
import {AdaptivityContext, getGUID, MODAL_TAKE_A_VIDEO} from "../assets/constants";

const MAX_VIDEO_SIZE_BYTES = 200 * 1024 * 1024;
const MAX_DURATION_MS = 3 * 60 * 1000;
const ALLOWED_EXTENSIONS = ['mp4', 'mov', 'm4v', 'webm', '3gp'];
const VIDEO_PREVIEW_MODAL = 'video_preview';

class TakeAVideo extends BaseComponent {

    constructor(props) {
        super(props);
        this.fileInputRef = React.createRef();
        this.videoRef = React.createRef();
        this.stream = null;
        this.mediaRecorder = null;
        this.chunks = [];
        this.timer = null;
        this.recordedBytes = 0;
        this.fromCamera = false;

        const hasAngles = Array.isArray(props.angles) && props.angles.length > 0;
        const hasFixedAngle = props.angleId !== undefined && props.angleId !== null;

        this.state = {
            ...this.state,
            useCamera: null,
            recording: false,
            elapsed: 0,
            blob: null,
            previewUrl: null,
            ext: 'mp4',
            selectedAngleId: hasFixedAngle ? Number(props.angleId) : null,
            angleStepDone: !hasAngles || hasFixedAngle
        };
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        this.cleanup();
    }

    cleanup = ({keepPreview = false} = {}) => {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }

        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            try {
                this.mediaRecorder.stop();
            } catch (e) {
                // already stopped
            }
        }
        this.mediaRecorder = null;
        this.chunks = [];
        this.recordedBytes = 0;

        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }

        if (this.videoRef.current) {
            this.videoRef.current.srcObject = null;
        }

        if (!keepPreview && this.state.previewUrl) {
            URL.revokeObjectURL(this.state.previewUrl);
        }
    }

    closeCapture = () => {
        this.cleanup();
        const hasAngles = Array.isArray(this.props.angles) && this.props.angles.length > 0;
        const hasFixedAngle = this.props.angleId !== undefined && this.props.angleId !== null;
        this.setState({
            useCamera: null,
            recording: false,
            elapsed: 0,
            blob: null,
            previewUrl: null,
            ext: 'mp4',
            selectedAngleId: hasFixedAngle ? Number(this.props.angleId) : null,
            angleStepDone: !hasAngles || hasFixedAngle
        });
        this.setActiveModal(null);
    }

    openCapture = () => {
        const hasAngles = Array.isArray(this.props.angles) && this.props.angles.length > 0;
        const hasFixedAngle = this.props.angleId !== undefined && this.props.angleId !== null;
        this.setState({
            useCamera: null,
            recording: false,
            elapsed: 0,
            blob: null,
            previewUrl: null,
            ext: 'mp4',
            selectedAngleId: hasFixedAngle ? Number(this.props.angleId) : null,
            angleStepDone: !hasAngles || hasFixedAngle
        });
        this.setActiveModal(MODAL_TAKE_A_VIDEO);
    }

    formatElapsed = (ms) => {
        const totalSec = Math.floor(ms / 1000);
        const min = String(Math.floor(totalSec / 60)).padStart(2, '0');
        const sec = String(totalSec % 60).padStart(2, '0');
        return `${min}:${sec}`;
    }

    pickMimeType = () => {
        const candidates = [
            'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp8,opus',
            'video/mp4',
            'video/webm'
        ];
        for (const type of candidates) {
            if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }
        return '';
    }

    extensionFromMime = (mime) => {
        if (!mime) return 'webm';
        if (mime.includes('mp4')) return 'mp4';
        if (mime.includes('webm')) return 'webm';
        return 'webm';
    }

    chooseCaptureMethod = (method) => {
        if (method === 'camera') {
            this.startCamera();
        } else if (method === 'upload') {
            this.setState({useCamera: null}, () => {
                if (this.fileInputRef.current) {
                    this.fileInputRef.current.click();
                }
            });
        }
    }

    startCamera = async () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.openErrorNoty('Камера недоступна в этом браузере');
            return;
        }

        if (typeof MediaRecorder === 'undefined') {
            this.openErrorNoty('Запись видео не поддерживается в этом браузере');
            return;
        }

        this.cleanup();
        this.fromCamera = true;

        const videoConstraints = {
            facingMode: 'environment',
            width: {ideal: 1920},
            height: {ideal: 1080}
        };

        let stream = null;
        let audioMissing = false;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                video: videoConstraints,
                audio: {echoCancellation: true, noiseSuppression: true}
            });
        } catch (e) {
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: videoConstraints,
                    audio: false
                });
                audioMissing = true;
            } catch (err) {
                console.error('getUserMedia failed', err);
                this.openErrorNoty('Не удалось получить доступ к камере');
                this.setState({useCamera: null});
                return;
            }
        }

        if (!audioMissing && stream.getAudioTracks().length === 0) {
            audioMissing = true;
        }

        this.stream = stream;
        this.enableContinuousFocus(stream);

        if (audioMissing) {
            console.warn('No audio track in stream');
            this.openErrorNoty('Микрофон недоступен — видео будет записано без звука', 4000);
        }
        this.setState({useCamera: true, recording: false, elapsed: 0}, () => {
            if (this.videoRef.current) {
                this.videoRef.current.srcObject = stream;
                this.videoRef.current.play().catch(() => {});
            }
        });
    }

    enableContinuousFocus = (stream) => {
        const track = stream && stream.getVideoTracks()[0];
        if (!track || typeof track.getCapabilities !== 'function') return;

        const caps = track.getCapabilities();
        if (caps && Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
            track.applyConstraints({advanced: [{focusMode: 'continuous'}]}).catch(() => {});
        }
    }

    startRecording = () => {
        if (!this.stream || this.state.recording) return;

        const mimeType = this.pickMimeType();
        const options = {
            ...(mimeType ? {mimeType} : {}),
            videoBitsPerSecond: 6 * 1024 * 1024,
            audioBitsPerSecond: 128 * 1024
        };
        this.chunks = [];
        this.recordedBytes = 0;

        try {
            this.mediaRecorder = new MediaRecorder(this.stream, options);
        } catch (e) {
            console.error('MediaRecorder create failed', e);
            this.openErrorNoty('Не удалось начать запись видео');
            return;
        }

        const ext = this.extensionFromMime(this.mediaRecorder.mimeType || mimeType);

        this.mediaRecorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
                this.chunks.push(event.data);
                this.recordedBytes += event.data.size;
                if (this.recordedBytes >= MAX_VIDEO_SIZE_BYTES) {
                    this.stopRecording();
                }
            }
        };

        this.mediaRecorder.onstop = () => {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }

            const blobType = (this.mediaRecorder && this.mediaRecorder.mimeType) || mimeType || 'video/webm';
            const blob = new Blob(this.chunks, {type: blobType});
            this.chunks = [];

            if (this.stream) {
                this.stream.getTracks().forEach(track => track.stop());
                this.stream = null;
            }
            if (this.videoRef.current) {
                this.videoRef.current.srcObject = null;
            }

            if (this.state.previewUrl) {
                URL.revokeObjectURL(this.state.previewUrl);
            }

            if (!blob.size) {
                this.openErrorNoty('Не удалось записать видео');
                this.setState({useCamera: null, recording: false, elapsed: 0, blob: null, previewUrl: null});
                return;
            }

            if (blob.size > MAX_VIDEO_SIZE_BYTES) {
                this.openErrorNoty('Видео слишком большое (максимум 200 МБ). Снимите ролик короче — примерно до 2 минут.');
                this.setState({useCamera: null, recording: false, elapsed: 0, blob: null, previewUrl: null});
                return;
            }

            const previewUrl = URL.createObjectURL(blob);
            this.setState({
                recording: false,
                blob,
                previewUrl,
                ext,
                useCamera: null
            });
            this.setActiveModal(VIDEO_PREVIEW_MODAL);
        };

        this.mediaRecorder.start(1000);
        const startedAt = Date.now();
        this.timer = setInterval(() => {
            const elapsed = Date.now() - startedAt;
            this.setState({elapsed});
            if (elapsed >= MAX_DURATION_MS) {
                this.stopRecording();
            }
        }, 250);

        this.setState({recording: true, elapsed: 0, ext});
    }

    stopRecording = () => {
        if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
            return;
        }
        try {
            this.mediaRecorder.stop();
        } catch (e) {
            console.error('MediaRecorder stop failed', e);
        }
    }

    handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;

        this.fromCamera = false;

        if (file.size > MAX_VIDEO_SIZE_BYTES) {
            this.openErrorNoty('Видео слишком большое (максимум 200 МБ). Снимите ролик короче — примерно до 2 минут.');
            return;
        }

        let ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            ext = 'mp4';
        }

        if (this.state.previewUrl) {
            URL.revokeObjectURL(this.state.previewUrl);
        }

        const previewUrl = URL.createObjectURL(file);
        this.setState({
            blob: file,
            previewUrl,
            ext,
            useCamera: null,
            recording: false,
            elapsed: 0
        });
        this.setActiveModal(VIDEO_PREVIEW_MODAL);
    }

    confirmVideo = () => {
        const {blob, ext} = this.state;
        if (!blob) return;

        const destination = this.props.destination ?? 'brands';
        const filename = getGUID() + '.' + (ext || 'mp4');
        const angleId = this.state.selectedAngleId !== null && this.state.selectedAngleId !== undefined
            ? Number(this.state.selectedAngleId)
            : (this.props.angleId ?? 0);

        window.postMessage({type: 'addVideoToQueue', blob, filename, destination});

        setTimeout(() => {
            window.postMessage({
                type: this.props.eventName ?? 'setSalePointMonitoringVideo',
                data: {
                    video: this.props.url + '/uploads/' + destination + '/' + filename,
                    filename,
                    angle_id: angleId
                }
            });
        }, 300);

        this.cleanup();
        const hasAngles = Array.isArray(this.props.angles) && this.props.angles.length > 0;
        const hasFixedAngle = this.props.angleId !== undefined && this.props.angleId !== null;
        this.setState({
            useCamera: null,
            recording: false,
            elapsed: 0,
            blob: null,
            previewUrl: null,
            ext: 'mp4',
            selectedAngleId: hasFixedAngle ? Number(this.props.angleId) : null,
            angleStepDone: !hasAngles || hasFixedAngle
        });
        this.setActiveModal(null);
    }

    retake = () => {
        if (this.state.previewUrl) {
            URL.revokeObjectURL(this.state.previewUrl);
        }
        this.setState({
            blob: null,
            previewUrl: null,
            recording: false,
            elapsed: 0
        });

        if (this.fromCamera) {
            this.setActiveModal(MODAL_TAKE_A_VIDEO);
            this.startCamera();
        } else {
            this.setState({useCamera: null});
            this.setActiveModal(MODAL_TAKE_A_VIDEO);
        }
    }

    renderCaptureBody = () => {
        const angles = this.props.angles || [];
        if (angles.length > 0 && !this.state.angleStepDone) {
            return (
                <Div>
                    <Title level="2" style={{marginBottom: 16, textAlign: 'center'}}>Укажите ракурс</Title>
                    {angles.map(angle => (
                        <SimpleCell
                            key={`video-angle-${angle.id}`}
                            onClick={() => this.setState({
                                selectedAngleId: Number(angle.id),
                                angleStepDone: true
                            })}
                        >
                            {angle.title}
                        </SimpleCell>
                    ))}
                </Div>
            );
        }

        if (this.state.useCamera === null) {
            return (
                <Div style={{display: 'flex', justifyContent: 'space-around', alignItems: 'center', height: '80vh'}}>
                    <Button mode="outline" onClick={() => this.chooseCaptureMethod('camera')}
                            style={{padding: '50x', fontSize: '50px'}}>
                        <Icon28CameraOutline width={100} height={100}/>
                        <Title>Камера</Title>
                    </Button>
                    <Button mode="outline" onClick={() => this.chooseCaptureMethod('upload')}>
                        <Icon28UploadOutline width={100} height={100} style={{margin: '50x'}}/>
                        <Title>Файл</Title>
                    </Button>
                </Div>
            );
        }

        return (
            <Div style={{position: 'relative', background: '#000', minHeight: '80vh'}}>
                <video
                    ref={this.videoRef}
                    muted
                    autoPlay
                    playsInline
                    style={{width: '100%', maxHeight: AdaptivityContext.h - 180, background: '#000'}}
                />
                <Div style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 12
                }}>
                    <Title level="2" style={{color: '#fff'}}>
                        {this.formatElapsed(this.state.elapsed)}
                        {this.state.recording ? ' / 03:00' : ''}
                    </Title>
                    {!this.state.recording ? (
                        <Button size="l" mode="primary" before={<Icon20RecordingCircleFill/>}
                                onClick={this.startRecording}>
                            Начать запись
                        </Button>
                    ) : (
                        <Button size="l" mode="destructive" before={<Icon24Stop/>}
                                onClick={this.stopRecording}>
                            Остановить
                        </Button>
                    )}
                    {!this.state.recording && (
                        <Button size="m" mode="tertiary" style={{color: '#fff'}}
                                onClick={() => {
                                    this.cleanup();
                                    this.setState({useCamera: null, recording: false, elapsed: 0});
                                }}>
                            Назад к выбору
                        </Button>
                    )}
                </Div>
            </Div>
        );
    }

    render() {
        const videoModals = (
            <ModalRoot activeModal={this.state.activeModal}>
                <ModalPage
                    hideCloseButton={true}
                    id={MODAL_TAKE_A_VIDEO}
                    onClose={this.closeCapture}
                    settlingHeight={100}
                    style={{width: '100%'}}
                    header={
                        <ModalPageHeader
                            after={<PanelHeaderButton onClick={this.closeCapture}><Icon24Cancel/></PanelHeaderButton>}>
                            Запись видео
                        </ModalPageHeader>
                    }
                >
                    <Group>
                        {this.renderCaptureBody()}
                        <input
                            type="file"
                            accept="video/*"
                            ref={this.fileInputRef}
                            onChange={this.handleFileChange}
                            style={{display: 'none'}}
                        />
                    </Group>
                </ModalPage>

                <ModalPage
                    hideCloseButton={true}
                    id={VIDEO_PREVIEW_MODAL}
                    onClose={this.closeCapture}
                    settlingHeight={100}
                    style={{width: '100%'}}
                    header={
                        <ModalPageHeader
                            after={<PanelHeaderButton onClick={this.closeCapture}><Icon24Cancel/></PanelHeaderButton>}>
                            Предпросмотр видео
                        </ModalPageHeader>
                    }
                >
                    <Group style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'start',
                        justifyContent: 'center',
                        textAlign: 'start'
                    }}>
                        {this.state.previewUrl && (
                            <video
                                src={this.state.previewUrl}
                                controls
                                playsInline
                                preload="metadata"
                                style={{width: '100%', maxHeight: AdaptivityContext.h - 180, background: '#000'}}
                            />
                        )}
                        <Button mode="outline" onClick={this.confirmVideo}
                                style={{
                                    color: 'lightgreen',
                                    position: 'absolute',
                                    left: '20px',
                                    bottom: '30%'
                                }} size="l"><Icon20Check/></Button>
                        <Button mode="outline" onClick={this.retake} style={{
                            color: 'orange',
                            position: 'absolute',
                            right: '20px',
                            bottom: '30%'
                        }} size="l"><Icon20RefreshOutline/></Button>
                    </Group>
                </ModalPage>
            </ModalRoot>
        );

        return (
            <SplitLayout modal={videoModals}>
                <SplitCol>
                    <Button
                        size={this.props.size ?? 'l'}
                        mode={this.props.mode ?? 'secondary'}
                        before={<Icon24Video/>}
                        onClick={this.openCapture}
                    >
                        {this.props.label ?? 'Снять видео'}
                    </Button>
                </SplitCol>
            </SplitLayout>
        );
    }
}

export default TakeAVideo;
